import {ChangeDetectionStrategy, Component, computed, effect, input, signal} from '@angular/core';
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {InputText} from "primeng/inputtext";
import {FloatLabel} from "primeng/floatlabel";
import {InputGroup} from "primeng/inputgroup";
import {InputGroupAddon} from "primeng/inputgroupaddon";
import {ButtonDirective, ButtonIcon} from "primeng/button";
import {merge} from "rxjs";

type Config = {
  type: "text";
  autocomplete?: "given-name" | "family-name" | 'street-address' | 'postal-code';
} | "email" | {
  type: "password";
  autocomplete?: "current-password" | "new-password";
};

@Component({
  selector: 'homey-text-input',
  imports: [
    ReactiveFormsModule,
    InputText,
    FloatLabel,
    InputGroup,
    InputGroupAddon,
    ButtonDirective,
    ButtonIcon
  ],
  templateUrl: './text-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class TextInputComponent {
  public readonly inputFormControl = input.required<FormControl<string>>();
  public readonly label = input.required<string>();
  public readonly inputId = input.required<string>();
  public readonly type = input.required<Config>();
  public readonly errorMessage = input<string>();
  public readonly showPasswordToggle = input(false);

  private readonly updateVisibleErrorMessage = signal<number>(0);
  protected readonly passwordVisible = signal(false);

  constructor() {
    effect(() => {
      const control = this.inputFormControl(); // read the current control
      const subscription = merge(control.statusChanges, control.valueChanges).subscribe(() =>
        this.updateVisibleErrorMessage.update(value => value + 1)
      );
      return () => subscription.unsubscribe();
    });
  }


  protected readonly visibleErrorMessage = computed(() => {
    // Read the status so that this computed re-runs on status changes
    this.updateVisibleErrorMessage();

    const control = this.inputFormControl();
    const errorMessage = this.errorMessage();

    // For example, show the error when invalid + touched
    return (errorMessage && control.invalid && control.dirty)
      ? errorMessage
      : undefined;
  });

  protected readonly autocomplete = computed(
    () => {
      const type = this.type();
      if (typeof type === "string") {
        switch (type) {
          case "email":
            return "email";
        }
      } else {
        return type.autocomplete;
      }
    }
  );

  protected readonly fieldType = computed(
    () => {
      const type = this.type();
      if (typeof type === "string") {
        return type;
      } else {
        return type.type;
      }
    }
  );

  protected readonly passwordType = computed(() => {
    if (this.fieldType() !== 'password' || !this.showPasswordToggle()) {
      return this.fieldType();
    }

    return this.passwordVisible() ? 'text' : 'password';
  });

  togglePasswordVisibility() {
    this.passwordVisible.update((current) => !current);
  }

  onFocusOut() {
    console.log('onFocusOut');
    this.updateVisibleErrorMessage.update(value => value + 1)
  }
}
